import type { ChartConfig } from "@/components/chart/types";
import { CHAT_ENTITY_CARD_SHELL_CLASS } from "./entityPreviewUi";
import type { ChatTimeseriesChartEmbed, ChatTimeseriesSeriesStream } from "./types";

export { CHAT_ENTITY_CARD_SHELL_CLASS };

/** Plot insets for floating-Ani timeseries embeds (outside built-in legend). */
export const CHAT_TIMESERIES_CHART_PADDING = {
  top: 10,
  right: 12,
  bottom: 28,
  left: 44,
} as const;

/** XYChart overrides for chat timeseries — does not change shared chart defaults. */
export const CHAT_TIMESERIES_XY_CONFIG: Partial<ChartConfig> = {
  padding: CHAT_TIMESERIES_CHART_PADDING,
  showLegend: false,
  layout: {
    honorConfigTheme: true,
  },
};

/** Column keys from worker tools → readable headers in chat tables. */
export function formatDataTableColumnHeader(column: string): string {
  if (!column) return column;
  if (column.includes(":") || column.includes(" ")) return column;
  const known: Record<string, string> = {
    portfolio_id: "Portfolio",
    fund: "Fund",
    security_name: "Security",
    weight_pct: "Weight",
    isin: "ISIN",
    asset_class: "Asset class",
    series: "Series",
    stream: "Stream",
    total_return_pct: "Total return",
    delta_contribution: "Δ contribution",
    delta_pp: "Δ (pp)",
  };
  if (known[column]) return known[column];
  return column
    .split("_")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(" ");
}

export function formatDataTableCellValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  const text = String(value);
  return text.trim() === "" ? "—" : text;
}

export function isNumericTableCell(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "—") return false;
  return /^-?\d[\d,]*(\.\d+)?%?$/.test(trimmed) || /^-?\d[\d,]*(\.\d+)?$/.test(trimmed);
}

export type TimeseriesChartSeriesInput = {
  id: string;
  label: string;
  points: Array<{ x: string; y: number }>;
  stream?: ChatTimeseriesSeriesStream;
};

const EMBED_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatEmbedDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : EMBED_DATE_FORMAT.format(parsed);
}

function describeSeriesStream(stream: ChatTimeseriesSeriesStream): string | null {
  if (!stream.label) return null;
  if (stream.estimate_from) {
    const officialPart = stream.last_real_date ? `${stream.label} to ${formatEmbedDate(stream.last_real_date)}` : stream.label;
    const estimateLabel = (stream.estimate_label || "estimated").toLowerCase();
    return `${officialPart}, ${estimateLabel} estimates from ${formatEmbedDate(stream.estimate_from)}`;
  }
  if (stream.fallback_from) {
    return `${stream.label} (no ${stream.fallback_from.replace(/_/g, " ")} data)`;
  }
  return null;
}

/** Interval plus any estimate or fallback disclosures, e.g. "Monthly · Official net to 31 Dec 2025, …". */
export function formatTimeseriesCaption(embed: Pick<ChatTimeseriesChartEmbed, "frequency" | "series">): string | null {
  const notes = embed.series
    .map((series) => ({ label: formatTimeseriesSeriesLabel(series.label), note: series.stream ? describeSeriesStream(series.stream) : null }))
    .filter((item): item is { label: string; note: string } => Boolean(item.note));
  const parts: string[] = [];
  if (embed.frequency) {
    parts.push(embed.frequency.charAt(0).toUpperCase() + embed.frequency.slice(1));
  }
  for (const { label, note } of notes) {
    parts.push(notes.length > 1 ? `${label}: ${note}` : note);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Legend label with the stream name, e.g. "MAC 5 · Official net". */
export function formatTimeseriesLegendLabel(label: string, stream?: ChatTimeseriesSeriesStream): string {
  const base = formatTimeseriesSeriesLabel(label);
  return stream?.label ? `${base} · ${stream.label}` : base;
}

export type SanitizedTimeseriesSeries = {
  key: string;
  label: string;
  points: Array<{ x: string; y: number }>;
};

const CHILD_BM_BENCHMARK_LABEL = /^Benchmark\s+child-bm-/i;

/** Fallback when API/worker still send internal benchmark ids in the label. */
export function formatTimeseriesSeriesLabel(label: string): string {
  if (CHILD_BM_BENCHMARK_LABEL.test(label)) {
    return "Benchmark";
  }
  return label;
}

function isValidTimeseriesPoint(point: { x: string; y: number }): boolean {
  if (!Number.isFinite(point.y)) {
    return false;
  }
  return !Number.isNaN(new Date(point.x).getTime());
}

/** Merge series into XYChart rows; coerce ISO date strings to Date for time scale. */
export function buildTimeseriesChartData(series: TimeseriesChartSeriesInput[]): {
  rows: Array<Record<string, string | number | Date>>;
  series: SanitizedTimeseriesSeries[];
} {
  const sanitized: SanitizedTimeseriesSeries[] = series
    .map((s) => ({
      label: formatTimeseriesLegendLabel(s.label, s.stream),
      points: s.points.filter(isValidTimeseriesPoint),
    }))
    .filter((s) => s.points.length > 0)
    .map((s, index) => ({
      key: `series_${index}`,
      label: s.label,
      points: s.points,
    }));

  const xValues = new Set<string>();
  for (const s of sanitized) {
    for (const point of s.points) {
      xValues.add(point.x);
    }
  }
  const xs = Array.from(xValues).sort((a, b) => {
    const da = new Date(a).getTime();
    const db = new Date(b).getTime();
    if (!Number.isNaN(da) && !Number.isNaN(db)) {
      return da - db;
    }
    return a.localeCompare(b);
  });
  const rows = xs.map((x) => {
    const parsed = new Date(x);
    const xValue = Number.isNaN(parsed.getTime()) ? x : parsed;
    const row: Record<string, string | number | Date> = { x: xValue };
    for (const s of sanitized) {
      const point = s.points.find((p) => p.x === x);
      if (point) {
        row[s.key] = point.y;
      }
    }
    return row;
  });
  return { rows, series: sanitized };
}

/** Shorten long legend labels with a middle ellipsis (default ~50% of characters visible). */
export function truncateMiddle(label: string, visibleRatio = 0.5): string {
  const maxVisible = Math.max(12, Math.floor(label.length * visibleRatio));
  if (label.length <= maxVisible) {
    return label;
  }
  const ellipsis = "…";
  const room = maxVisible - ellipsis.length;
  if (room < 2) {
    return `${label.slice(0, maxVisible - 1)}${ellipsis}`;
  }
  const head = Math.ceil(room / 2);
  const tail = Math.floor(room / 2);
  return `${label.slice(0, head)}${ellipsis}${label.slice(-tail)}`;
}

export function formatEmbedUnits(units?: string | Record<string, string>): string | null {
  if (!units) return null;
  if (typeof units === "string") return units.trim() || null;
  const parts = Object.entries(units)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`);
  return parts.length ? parts.join(" · ") : null;
}
